import { useState, useEffect } from 'react';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function DemoRescheduleModal({ demo, onClose, onSuccess }) {
  const currentDate = demo?.payload?.dateTime?.demoDate || '';
  const currentTime = demo?.payload?.dateTime?.timeSlot || '';
  
  // Normalize the current time to canonical format
  const normalizeTime = (timeSlot) => {
    const timeNormalizeMap = {
      'Morning': 'Morning',
      '9:00 AM - 12:00 PM': 'Morning',
      'Afternoon': 'Afternoon',
      '1:00 PM - 4:00 PM': 'Afternoon',
      'Evening': 'Evening',
      '5:00 PM - 8:00 PM': 'Evening'
    };
    return timeNormalizeMap[timeSlot] || timeSlot;
  };
  
  const [newDate, setNewDate] = useState(currentDate);
  const [newTime, setNewTime] = useState(normalizeTime(currentTime));
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  const timeSlots = [
    'Morning',
    'Afternoon',
    'Evening'
  ];

  // Format date to "Jan 5, 2026" format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Automatically check for conflicts when date or time changes
  useEffect(() => {
    if (newDate && newTime) {
      checkForConflicts();
    }
  }, [newDate, newTime]);

  const checkForConflicts = async () => {
    if (!newDate || !newTime) return;

    // Support both old and new field structures
    const agentId = demo?.payload?.agentCode;
    if (!agentId) return;

    try {
      setCheckingConflicts(true);
      const demosRef = collection(db, 'demo_bookings');
      
      // Query all demos for this agent on this date (can't use compound query with timeSlot due to format variations)
      // Need to check both old and new field structures
      const q = query(
        demosRef,
        where('payload.dateTime.demoDate', '==', newDate)
      );

      const snapshot = await getDocs(q);
      const conflictingDemos = [];
      
      // Map to normalize time formats for comparison
      const timeNormalizeMap = {
        'Morning': 'Morning',
        '9:00 AM - 12:00 PM': 'Morning',
        'Afternoon': 'Afternoon',
        '1:00 PM - 4:00 PM': 'Afternoon',
        'Evening': 'Evening',
        '5:00 PM - 8:00 PM': 'Evening'
      };
      
      const normalizedNewTime = timeNormalizeMap[newTime] || newTime;
      
      snapshot.forEach((doc) => {
        const docData = doc.data();
        const docAgentId = docData?.payload?.agentCode;
        const docTimeSlot = docData?.payload?.dateTime?.timeSlot;
        const normalizedDocTime = timeNormalizeMap[docTimeSlot] || docTimeSlot;
        
        // Don't count the current demo as a conflict
        if (doc.id !== demo.id) {
          // Check if same agent and times conflict (after normalization)
          if (docAgentId === agentId && normalizedDocTime === normalizedNewTime) {
            conflictingDemos.push({
              id: doc.id,
              ...docData
            });
          }
        }
      });
      
      setConflicts(conflictingDemos);
    } catch (err) {
      console.error('Error checking conflicts:', err);
    } finally {
      setCheckingConflicts(false);
    }
  };

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      setError('Please select both date and time');
      return;
    }

    if (newDate === currentDate && newTime === normalizeTime(currentTime)) {
      setError('Please select a different date or time');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Final conflict check before saving
      const agentId = demo?.payload?.agentCode;
      if (agentId) {
        const demosRef = collection(db, 'demo_bookings');
        const q = query(
          demosRef,
          where('payload.dateTime.demoDate', '==', newDate)
        );

        const snapshot = await getDocs(q);
        
        const timeNormalizeMap = {
          'Morning': 'Morning',
          '9:00 AM - 12:00 PM': 'Morning',
          'Afternoon': 'Afternoon',
          '1:00 PM - 4:00 PM': 'Afternoon',
          'Evening': 'Evening',
          '5:00 PM - 8:00 PM': 'Evening'
        };
        
        const normalizedNewTime = timeNormalizeMap[newTime] || newTime;
        let hasConflict = false;
        
        snapshot.forEach((doc) => {
          if (doc.id !== demo.id) {
            const docData = doc.data();
            const docAgentId = docData?.payload?.agentCode;
            const docTimeSlot = docData?.payload?.dateTime?.timeSlot;
            const normalizedDocTime = timeNormalizeMap[docTimeSlot] || docTimeSlot;
            
            if (docAgentId === agentId && normalizedDocTime === normalizedNewTime) {
              hasConflict = true;
            }
          }
        });

        if (hasConflict) {
          setError('Cannot reschedule: Agent has a conflicting demo at this time. Please refresh and try again.');
          setLoading(false);
          return;
        }
      }

      const demoRef = doc(db, 'demo_bookings', demo.id);
      
      await updateDoc(demoRef, {
        'payload.dateTime.demoDate': newDate,
        'payload.dateTime.timeSlot': newTime,
        'payload.rescheduleReason': reason || 'Rescheduled by admin',
        'updated_at': new Date(),
        'rescheduled_at': new Date()
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error rescheduling demo:', err);
      setError('Failed to reschedule demo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Reschedule Demo</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Demo Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Demo Details</h3>
            <div className="space-y-1 text-sm">
              <p><span className="font-medium">Customer:</span> {demo?.payload?.customer?.name || 'N/A'}</p>
              <p><span className="font-medium">Agent:</span> {demo?.payload?.agentCode || 'N/A'}</p>
              <p><span className="font-medium">Location:</span> {demo?.payload?.location || 'N/A'}</p>
            </div>
          </div>

          {/* Current Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Schedule
            </label>
            <div className="px-3 py-2 bg-gray-100 rounded-md text-sm text-gray-900">
              {formatDate(currentDate)} in the {normalizeTime(currentTime)}
            </div>
          </div>

          {/* New Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Date
            </label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => {
                setNewDate(e.target.value);
                setConflicts([]);
              }}
              min={new Date().toISOString().split('T')[0]}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
            {newDate && (
              <p className="mt-1 text-sm text-gray-600">
                Selected: {formatDate(newDate)}
              </p>
            )}
          </div>

          {/* New Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Time Slot
            </label>
            <select
              value={newTime}
              onChange={(e) => {
                setNewTime(e.target.value);
                setConflicts([]);
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">-- Select time slot --</option>
              {timeSlots.map((slot) => (
                <option key={slot} value={slot}>{slot}</option>
              ))}
            </select>
          </div>

          {/* Check Conflicts Button */}
          {newDate && newTime && (
            <button
              onClick={checkForConflicts}
              disabled={checkingConflicts}
              className="w-full px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:bg-gray-100 disabled:text-gray-500"
            >
              {checkingConflicts ? 'Checking...' : 'Check for Conflicts'}
            </button>
          )}

          {/* Conflicts Warning */}
          {conflicts.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Scheduling Conflict!</p>
                  <p className="text-sm text-red-700 mt-1">
                    Agent already has {conflicts.length} demo(s) at this time:
                  </p>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    {conflicts.map((conflict) => (
                      <li key={conflict.id}>
                        {conflict.payload?.customer?.name || 'Unknown customer'}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* No Conflicts */}
          {newDate && newTime && conflicts.length === 0 && !checkingConflicts && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm text-green-800">
                ✓ No conflicts found. Safe to reschedule.
              </p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Rescheduling (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Enter reason for rescheduling..."
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleReschedule}
            disabled={loading || !newDate || !newTime || conflicts.length > 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Rescheduling...' : 'Reschedule Demo'}
          </button>
        </div>
      </div>
    </div>
  );
}
